import { apiClient } from './api'
import {
  JobProject,
  JobProjectListResponse,
  JobProjectWithHistory,
  CreateJobProjectRequest
} from '../types/jobProject'

import type { MeasurementSystem } from '../lib/measurementSystem'

export const jobProjectApi = {
  updateProject: async (jobId: string, values: { reference: string; name: string; description: string }) => {
    const { data } = await apiClient.patch<{ project: JobProject }>(
      `/optimization/v1/job-projects/${encodeURIComponent(jobId)}`, values
    )
    return data.project
  },
  /** Updates the project choice without starting an optimization. */
  updateMeasurementSystem: async (jobId: string, measurementSystem: MeasurementSystem) => {
    const { data } = await apiClient.patch<{ project: JobProject }>(
      `/optimization/v1/job-projects/${encodeURIComponent(jobId)}/measurement-system`,
      { measurement_system: measurementSystem }
    )
    return data.project
  },
  /**
   * Liste les projets de chargement
   */
  listProjects: async (params?: {
    status?: JobProject['status']
    search?: string
    limit?: number
    offset?: number
  }) => {
    const query: Record<string, string | number> = {}
    if (params?.status) query.status = params.status
    if (params?.search) query.search = params.search
    if (params?.limit != null) query.limit = params.limit
    if (params?.offset != null) query.offset = params.offset

    const { data } = await apiClient.get<JobProjectListResponse>(
      '/optimization/v1/job-projects',
      { params: query }
    )
    return data
  },

  /**
   * Crée un nouveau projet de chargement
   */
  createProject: async (project: CreateJobProjectRequest) => {
    const { data } = await apiClient.post<{ project: JobProject }>(
      '/optimization/v1/job-projects',
      project
    )
    return data.project
  },

  /**
   * Récupère un projet avec son run courant
   */
  getProject: async (jobId: string) => {
    const { data } = await apiClient.get<{ project: JobProject }>(
      `/optimization/v1/job-projects/${jobId}`
    )
    return data.project
  },

  /**
   * Récupère l'historique des runs d'un projet
   */
  getProjectRuns: async (jobId: string, params?: { limit?: number; offset?: number }) => {
    const { data } = await apiClient.get<JobProjectWithHistory>(
      `/optimization/v1/job-projects/${jobId}/runs`,
      { params }
    )
    return data
  },

  /**
   * Met à jour le statut d'un projet
   */
  updateProjectStatus: async (jobId: string, status: JobProject['status']) => {
    const { data } = await apiClient.patch<{ project: JobProject }>(
      `/optimization/v1/job-projects/${jobId}/status`,
      { status }
    )
    return data.project
  },

  /**
   * Archive un projet
   */
  archiveProject: async (jobId: string) => {
    return jobProjectApi.updateProjectStatus(jobId, 'archived')
  },

  /**
   * Supprime un projet de chargement
   */
  deleteProject: async (jobId: string) => {
    const { data } = await apiClient.delete<{ success: boolean }>(
      `/optimization/v1/job-projects/${jobId}`
    )
    return data
  },

  /**
   * Supprime une version (run) d'un projet
   */
  deleteRun: async (jobId: string, runId: string) => {
    const { data } = await apiClient.delete<{ success: boolean }>(
      `/optimization/v1/job-projects/${jobId}/runs/${runId}`
    )
    return data
  },

  /**
   * Duplique un projet de chargement avec ses inputs (cargaison, équipement, règles)
   * Ne copie PAS les résultats
   */
  duplicateProject: async (jobId: string, name?: string) => {
    const { data } = await apiClient.post<{ project: JobProject }>(
      `/optimization/v1/job-projects/${jobId}/duplicate`,
      name ? { name } : {}
    )
    return data.project
  }
}
